import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Sidebar } from '../dashboard_component';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { cbtAPI, labelAPI } from '../../api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const CbtCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedLabelId = searchParams.get('label_id');

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [showDuration, setShowDuration] = useState(true);
  const [showTimeSettings, setShowTimeSettings] = useState(false);
  const [availableLabels, setAvailableLabels] = useState([]);

  const [cbtData, setCbtData] = useState({
    cbt_name: "",
    description: "",
    instructions: "",
    cbt_duration: 60,
    attempt_limit: 1,
    default_score: 1,
    max_questions: 0,
    cbt_status: 'draft',
    access_type: 'open_access',
    label_id: preselectedLabelId || null,
    // Additional fields (matching backend)
    start_time: null,
    end_time: null,
    allow_retake_after: 0,
    randomize_questions: false,
    randomize_options: false,
    question_selection: "all",
    show_question_one_by_one: false,
    allow_back_navigation: true,
    allow_question_review: true,
    passing_score: 0,
    negative_marking: false,
    negative_mark_percentage: 0,
    show_score_immediately: true,
    show_correct_answers: false,
    show_student_answers: true,
    show_explanation: false,
    allow_answer_review: true,
    require_webcam: false,
    allow_fullscreen_exit: false,
    allow_copy_paste: false,
    enable_auto_submit: true,
    max_inactivity_time: 5,
    sections: []
  });

  const handleToggle = () => setIsCollapsed(!isCollapsed);

  useEffect(() => {
    const fetchLabels = async () => {
      try {
        const res = await labelAPI.getLabels();
        setAvailableLabels(res.data || []);
      } catch (error) {
        console.error('Failed to fetch labels', error);
      }
    };
    fetchLabels();
  }, []);

  const handleInputChange = (field, value) => {
    setCbtData(prev => ({ ...prev, [field]: value }));
  };

  const handleNumberChange = (field, value) => {
    const numValue = parseInt(value) || 0;
    setCbtData(prev => ({ ...prev, [field]: numValue }));
  };

  const handleBooleanChange = (field, value) => {
    setCbtData(prev => ({ ...prev, [field]: value === 'true' || value === true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cbtData.cbt_name.trim()) {
      toast.error('CBT name is required');
      return;
    }

    const submitData = {
      ...cbtData,
      cbt_duration: showDuration ? cbtData.cbt_duration : 0,
      start_time: showTimeSettings ? cbtData.start_time : null,
      end_time: showTimeSettings ? cbtData.end_time : null
    };

    setLoading(true);
    try {
      const response = await cbtAPI.createCBT(submitData);
      if (response.success) {
        toast.success('CBT created successfully!');
        navigate(`/cbt_creator/cbt-edit/${response.data.cbt_data_id}`);
      } else {
        toast.error(response.error || 'Failed to create CBT');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create CBT');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: '📝' },
    { id: 'timing', label: 'Timing', icon: '⏰' },
    { id: 'questions', label: 'Questions', icon: '❓' },
    { id: 'scoring', label: 'Scoring', icon: '🏆' },
    { id: 'display', label: 'Display', icon: '👁️' },
    { id: 'security', label: 'Security', icon: '🔒' }
  ];

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar isCollapsed={isCollapsed} onToggle={handleToggle} />
      <div className={`flex-1 transition-all duration-300 pt-16 lg:pt-8 ${isCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6 flex items-center gap-4">
            <button
              onClick={() => navigate('/cbt_creator/cbts')}
              className="text-gray-600 hover:text-gray-900 flex items-center gap-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to CBTs
            </button>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
              <h1 className="text-2xl font-bold text-white">Create New CBT</h1>
              <p className="text-blue-100 text-sm mt-1">Configure your test settings</p>
            </div>

            {/* Tab Navigation */}
            <div className="flex overflow-x-auto border-b border-gray-200 bg-gray-50 px-4">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit}>
              <div className="p-6 space-y-6">
                {/* Basic Info Tab */}
                {activeTab === 'basic' && (
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">CBT Name *</label>
                      <input
                        type="text"
                        value={cbtData.cbt_name}
                        onChange={(e) => handleInputChange('cbt_name', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
                      <textarea
                        rows="3"
                        value={cbtData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500"
                        placeholder="Optional description"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Instructions</label>
                      <textarea
                        rows="3"
                        value={cbtData.instructions}
                        onChange={(e) => handleInputChange('instructions', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        placeholder="Instructions for students"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Folder (Label)</label>
                      <select
                        value={cbtData.label_id || ''}
                        onChange={(e) => handleInputChange('label_id', e.target.value || null)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="">No Folder (Default)</option>
                        {availableLabels.map(label => (
                          <option key={label.label_id} value={label.label_id}>
                            {label.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Default Score</label>
                        <input
                          type="number"
                          step="0.1"
                          value={cbtData.default_score}
                          onChange={(e) => handleNumberChange('default_score', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Max Attempts</label>
                        <input
                          type="number"
                          value={cbtData.attempt_limit}
                          onChange={(e) => handleNumberChange('attempt_limit', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                          min="1"
                        />
                      </div>
                    </div>
                    <div className="flex gap-6">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={cbtData.cbt_status === 'draft'}
                          onChange={() => handleInputChange('cbt_status', 'draft')}
                          className="mr-2"
                        />
                        Draft
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={cbtData.cbt_status === 'published'}
                          onChange={() => handleInputChange('cbt_status', 'published')}
                          className="mr-2"
                        />
                        Published
                      </label>
                    </div>
                    <div className="flex gap-6">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={cbtData.access_type === 'open_access'}
                          onChange={() => handleInputChange('access_type', 'open_access')}
                          className="mr-2"
                        />
                        Open Access
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={cbtData.access_type === 'close_access'}
                          onChange={() => handleInputChange('access_type', 'close_access')}
                          className="mr-2"
                        />
                        Close Access
                      </label>
                    </div>
                  </div>
                )}

                {/* Timing Tab */}
                {activeTab === 'timing' && (
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-semibold mb-2">Duration</label>
                      <div className="flex gap-4 mb-3">
                        <label className="flex items-center">
                          <input type="radio" checked={!showDuration} onChange={() => setShowDuration(false)} className="mr-2" />
                          No Limit
                        </label>
                        <label className="flex items-center">
                          <input type="radio" checked={showDuration} onChange={() => setShowDuration(true)} className="mr-2" />
                          Set Duration
                        </label>
                      </div>
                      {showDuration && (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={cbtData.cbt_duration}
                            onChange={(e) => handleNumberChange('cbt_duration', e.target.value)}
                            className="w-32 px-3 py-2 border border-gray-300 rounded-lg"
                            min="1"
                          />
                          <span>minutes</span>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Scheduled Time</label>
                      <label className="flex items-center mb-3">
                        <input type="checkbox" checked={showTimeSettings} onChange={() => setShowTimeSettings(!showTimeSettings)} className="mr-2" />
                        Enable scheduled start/end
                      </label>
                      {showTimeSettings && (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm text-gray-600">Start Time</label>
                            <DatePicker
                              selected={cbtData.start_time}
                              onChange={(date) => handleInputChange('start_time', date)}
                              showTimeSelect
                              dateFormat="MMM d, yyyy h:mm aa"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                              placeholderText="Select start"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-gray-600">End Time</label>
                            <DatePicker
                              selected={cbtData.end_time}
                              onChange={(date) => handleInputChange('end_time', date)}
                              showTimeSelect
                              dateFormat="MMM d, yyyy h:mm aa"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                              placeholderText="Select end"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-1">Retake After (hours)</label>
                      <input
                        type="number"
                        value={cbtData.allow_retake_after}
                        onChange={(e) => handleNumberChange('allow_retake_after', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        min="0"
                      />
                    </div>
                  </div>
                )}

                {/* Questions Tab */}
                {activeTab === 'questions' && (
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-semibold mb-1">Max Questions (0 = all)</label>
                      <input
                        type="number"
                        value={cbtData.max_questions}
                        onChange={(e) => handleNumberChange('max_questions', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        min="0"
                      />
                    </div>
                    <div className="space-y-3">
                      <ToggleSwitch
                        label="Randomize Questions"
                        checked={cbtData.randomize_questions}
                        onChange={(val) => handleBooleanChange('randomize_questions', val)}
                      />
                      <ToggleSwitch
                        label="Randomize Options"
                        checked={cbtData.randomize_options}
                        onChange={(val) => handleBooleanChange('randomize_options', val)}
                      />
                      <ToggleSwitch
                        label="Show Questions One by One"
                        checked={cbtData.show_question_one_by_one}
                        onChange={(val) => handleBooleanChange('show_question_one_by_one', val)}
                      />
                      <ToggleSwitch
                        label="Allow Back Navigation"
                        checked={cbtData.allow_back_navigation}
                        onChange={(val) => handleBooleanChange('allow_back_navigation', val)}
                      />
                      <ToggleSwitch
                        label="Allow Question Review"
                        checked={cbtData.allow_question_review}
                        onChange={(val) => handleBooleanChange('allow_question_review', val)}
                      />
                    </div>
                  </div>
                )}

                {/* Scoring Tab */}
                {activeTab === 'scoring' && (
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-semibold mb-1">Passing Score (0 = none)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={cbtData.passing_score}
                        onChange={(e) => handleNumberChange('passing_score', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <ToggleSwitch
                      label="Enable Negative Marking"
                      checked={cbtData.negative_marking}
                      onChange={(val) => handleBooleanChange('negative_marking', val)}
                    />
                    {cbtData.negative_marking && (
                      <div>
                        <label className="block text-sm font-semibold mb-1">Negative Mark %</label>
                        <input
                          type="number"
                          value={cbtData.negative_mark_percentage}
                          onChange={(e) => handleNumberChange('negative_mark_percentage', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                          min="0"
                          max="100"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Display Tab */}
                {activeTab === 'display' && (
                  <div className="space-y-3">
                    <ToggleSwitch
                      label="Show Score Immediately"
                      checked={cbtData.show_score_immediately}
                      onChange={(val) => handleBooleanChange('show_score_immediately', val)}
                    />
                    <ToggleSwitch
                      label="Show Correct Answers"
                      checked={cbtData.show_correct_answers}
                      onChange={(val) => handleBooleanChange('show_correct_answers', val)}
                    />
                    <ToggleSwitch
                      label="Show Student Answers"
                      checked={cbtData.show_student_answers}
                      onChange={(val) => handleBooleanChange('show_student_answers', val)}
                    />
                    <ToggleSwitch
                      label="Show Explanation"
                      checked={cbtData.show_explanation}
                      onChange={(val) => handleBooleanChange('show_explanation', val)}
                    />
                    <ToggleSwitch
                      label="Allow Answer Review"
                      checked={cbtData.allow_answer_review}
                      onChange={(val) => handleBooleanChange('allow_answer_review', val)}
                    />
                  </div>
                )}

                {/* Security Tab */}
                {activeTab === 'security' && (
                  <div className="space-y-5">
                    <div className="space-y-3">
                      <ToggleSwitch
                        label="Require Webcam Monitoring"
                        checked={cbtData.require_webcam}
                        onChange={(val) => handleBooleanChange('require_webcam', val)}
                      />
                      <ToggleSwitch
                        label="Allow Fullscreen Exit"
                        checked={cbtData.allow_fullscreen_exit}
                        onChange={(val) => handleBooleanChange('allow_fullscreen_exit', val)}
                      />
                      <ToggleSwitch
                        label="Allow Copy/Paste"
                        checked={cbtData.allow_copy_paste}
                        onChange={(val) => handleBooleanChange('allow_copy_paste', val)}
                      />
                      <ToggleSwitch
                        label="Enable Auto-Submit"
                        checked={cbtData.enable_auto_submit}
                        onChange={(val) => handleBooleanChange('enable_auto_submit', val)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-1">Max Inactivity (minutes)</label>
                      <input
                        type="number"
                        value={cbtData.max_inactivity_time}
                        onChange={(e) => handleNumberChange('max_inactivity_time', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        min="1"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
                <button
                  type="button"
                  onClick={() => navigate('/cbt_creator/cbts')}
                  className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {loading && <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>}
                  Create CBT
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

// Reusable Toggle Switch
const ToggleSwitch = ({ label, checked, onChange }) => (
  <label className="flex items-center justify-between cursor-pointer">
    <span className="text-gray-700">{label}</span>
    <div className="relative inline-block w-11 h-6">
      <input
        type="checkbox"
        className="opacity-0 w-0 h-0"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className={`absolute inset-0 rounded-full transition-colors ${checked ? 'bg-blue-600' : 'bg-gray-300'}`}>
        <span className={`absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform ${checked ? 'transform translate-x-5' : ''}`} />
      </span>
    </div>
  </label>
);

export default CbtCreate;